import React, { useCallback, useState } from 'react';
import { VideoOverlayObject, Coordinate, PivotOption } from './VideoTypes';
import useInterval from '../Util/useInterval';
import { isFirefox } from '../Util/Browser';

const mouseUpdateIntervalMs: number = 40;

const getPivotCoord = (obj: VideoOverlayObject, width: number, height: number) => {
  switch(obj.pivot) {
    case PivotOption.TopLeft:
      return obj.center;
    case PivotOption.TopCenter:
        return {x: obj.center.x - width*0.5, y: obj.center.y};
    case PivotOption.TopRight:
      return {x: obj.center.x - width, y: obj.center.y};
    case PivotOption.MidLeft:
      return {x: obj.center.x, y: obj.center.y - height*0.5};
    case PivotOption.MidCenter:
        return {x: obj.center.x - width*0.5, y: obj.center.y - height*0.5};
    case PivotOption.MidRight:
      return {x: obj.center.x - width, y: obj.center.y - height*0.5};
    case PivotOption.BottomLeft:
      return {x: obj.center.x, y: obj.center.y - height};
    case PivotOption.BottomCenter:
        return {x: obj.center.x - width*0.5, y: obj.center.y - height};
    case PivotOption.BottomRight:
      return {x: obj.center.x - width, y: obj.center.y - height};
    default:
      return {x: obj.center.x - width*0.5, y: obj.center.y - height*0.5};
  };
};

type Props = {
  width: number;
  height: number;
  svgScale: number;
  onMouseDown: (mousePosition: Coordinate, event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onMouseUp: (mousePosition: Coordinate, event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onMouseMove: (mousePosition: Coordinate) => void;
  onMouseLeave?: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  overlayObjects?: VideoOverlayObject[];
  mouseObjects?: VideoOverlayObject[];
  viewScaleFirefox?: number;
  xOffsetFirefox?: number;
  yOffsetFirefox?: number;
};

const ViewOverlayCanvas = (props: Props) => {
  const {
    width,
    height,
    svgScale,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    onMouseLeave,
    overlayObjects,
    mouseObjects,
    viewScaleFirefox,
    xOffsetFirefox,
    yOffsetFirefox,
  } = props;

  const [mouseTarget, setMouseTarget] = useState<SVGSVGElement | undefined>(undefined);
  const [mousePosition, setMousePosition] = useState<Coordinate | undefined>(undefined);
  const [imagePosition, setImagePosition] = useState<Coordinate | undefined>(undefined);

  const handleMouseMove = useCallback((event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setMouseTarget(event.currentTarget);
    const mousePosition: Coordinate = {x: event.clientX, y: event.clientY};
    setMousePosition(mousePosition);
  }, []);

  const handleMouseLeave = useCallback((event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setMousePosition(undefined);
    setImagePosition(undefined);
    onMouseLeave && onMouseLeave(event);
  }, [onMouseLeave]);

  const handleMouseDown = useCallback((event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if(!imagePosition || !onMouseDown) return;
    onMouseDown(imagePosition, event);
  }, [imagePosition, onMouseDown]);

  const handleMouseUp = useCallback((event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if(!imagePosition || !onMouseUp) return;
    onMouseUp(imagePosition, event);
  }, [imagePosition, onMouseUp]);

  useInterval(() => {
    if(!mousePosition || !onMouseMove || !mouseTarget) return;

    if(mouseTarget.getScreenCTM()) {
      const ctm: DOMMatrix | null = mouseTarget.getScreenCTM();

      if(ctm) {
        
        // If we are using firefox, change our transformation matrix.
        if(isFirefox) {
          if(viewScaleFirefox) {
            const r = mouseTarget.getBoundingClientRect();

            ctm.a = ctm.a * viewScaleFirefox;
            ctm.d = ctm.d * viewScaleFirefox;
            if(r) {
              ctm.e = r.x + (xOffsetFirefox||0) * viewScaleFirefox;
              ctm.f = r.y + (yOffsetFirefox||0) * viewScaleFirefox;
            }
          }
        }
        
        let svgPos = mouseTarget.createSVGPoint();
        svgPos.x = mousePosition.x;
        svgPos.y = mousePosition.y;
        svgPos = svgPos.matrixTransform(ctm.inverse());
        setImagePosition(svgPos);
        onMouseMove(svgPos);
      }
    }
  }, mouseUpdateIntervalMs);

  let svgId: number = 0;
  const overlaySvg: JSX.Element[] = overlayObjects 
    ? overlayObjects.map(
      (obj: VideoOverlayObject) => {
        if(obj.disableAutoScale) {
          return (
            <svg
              key={svgId++}
            >
              {obj.svg}
            </svg>
          );
        } else {
          const scaledWidth: number = obj.width * svgScale;
          const scaledHeight: number = obj.height * svgScale;
          const viewBox: string = '0 0 ' + scaledWidth + ' ' + scaledHeight;

          const pivotCoord: Coordinate = getPivotCoord(obj, scaledWidth, scaledHeight);
          return (
            <svg
              key={svgId++}
              width={scaledWidth}
              height={scaledHeight}
              viewBox={viewBox}
              x={pivotCoord.x}
              y={pivotCoord.y}
            >
              {obj.svg}
            </svg>
          );
        }
      }
    ) : [];

  // overlay mouse item
  const mouseSvg: JSX.Element | undefined = imagePosition && mouseObjects && (
    <svg>
      {mouseObjects.map(obj => {
        const scaledWidth: number = obj.width * svgScale;
        const scaledHeight: number = obj.height * svgScale;
        const pivotCoord: Coordinate = getPivotCoord(obj, scaledWidth, scaledHeight);
        return <svg
          key={svgId++}
          width={obj.width * svgScale}
          height={obj.height * svgScale}
          viewBox={'0 0 ' + obj.width + ' ' + obj.height}
          x={imagePosition.x + pivotCoord.x}
          y={imagePosition.y + pivotCoord.y}
        >
          {obj.svg}
        </svg>
      })}
    </svg>

  )

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={'0 0 ' + width + ' ' + height}
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'fixed' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {
        // overlay svg items
        overlaySvg
      }

      {
        // overlay mouse item
        mouseSvg
      }
    </svg>
  );
};

export default ViewOverlayCanvas;